<template>
  <!-- i18n(2B): campo localizado con tabs ES / EN / PT.
       Pablo edita ES; EN/PT empiezan vacios y la Cloud Function (Fase 3)
       los puebla via DeepL al guardar. Si Pablo edita EN/PT a mano se
       activa `manualOverride[lang]=true` y la Cloud Function ya no pisa
       ese campo. -->
  <div class="i18n-field">
    <label v-if="label" class="i18n-field__label">{{ label }}</label>

    <ul class="i18n-field__tabs">
      <li
        v-for="lang in ['es', 'en', 'pt']"
        :key="lang"
        :class="[
          'i18n-field__tab',
          { 'i18n-field__tab--active': activeLang === lang },
          { 'i18n-field__tab--override': lang !== 'es' && isOverride(lang) },
        ]"
        @click="activeLang = lang"
      >
        <span>{{ lang.toUpperCase() }}</span>
        <small
          v-if="lang !== 'es' && isOverride(lang)"
          title="Edicion manual: la Cloud Function no la sobreescribira"
        >
          ✎
        </small>
        <small
          v-else-if="lang !== 'es' && isEmpty(lang)"
          class="i18n-field__pending"
          title="Se autotraduce al guardar"
        >
          auto
        </small>
      </li>
    </ul>

    <input
      v-if="type === 'text'"
      class="form-control"
      :value="currentText"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="emitChange($event.target.value)"
    />

    <textarea
      v-else-if="type === 'textarea'"
      class="form-control"
      :rows="rows"
      :value="currentText"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="emitChange($event.target.value)"
    />

    <VueEditor
      v-else-if="type === 'quill'"
      :key="`quill-${activeLang}`"
      :value="currentText || ''"
      :editor-toolbar="quillToolbar"
      @text-change="onQuillChange"
    />

    <div v-else-if="type === 'array-strings'" class="i18n-field__array">
      <ul v-if="currentArray.length > 0" class="i18n-field__items">
        <li v-for="(item, index) in currentArray" :key="index" class="i18n-field__item">
          <span class="i18n-field__item-text">{{ item }}</span>
          <button type="button" class="i18n-field__remove" @click="removeItem(index)">×</button>
        </li>
      </ul>
      <p v-else class="i18n-field__empty">
        <em>(vacio)</em>
      </p>
      <div class="i18n-field__add">
        <input
          type="text"
          class="form-control"
          v-model="newItem"
          :placeholder="placeholder || 'Agregar item'"
          @keyup.enter="addItem"
        />
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          @click="addItem"
          :disabled="!newItem.trim()"
        >
          Agregar
        </button>
      </div>
    </div>

    <div v-else-if="type === 'array-html'" class="i18n-field__array">
      <ul v-if="currentArray.length > 0" class="i18n-field__items">
        <li v-for="(item, index) in currentArray" :key="index" class="i18n-field__item">
          <div class="i18n-field__item-text" v-html="item"></div>
          <button type="button" class="i18n-field__remove" @click="removeItem(index)">×</button>
        </li>
      </ul>
      <p v-else class="i18n-field__empty">
        <em>(vacio)</em>
      </p>
      <div class="i18n-field__add i18n-field__add--vertical">
        <VueEditor
          :key="`array-html-${activeLang}`"
          v-model="newItem"
          :editor-toolbar="quillToolbar"
          @input="(html) => { this.newItem = html; }"
        />
        <button
          type="button"
          class="btn btn-outline-primary btn-sm mt-2"
          @click="addItem"
          :disabled="false"
        >
          Agregar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { VueEditor } from "vue2-editor";

/**
 * Props
 *   label              : Etiqueta del campo
 *   type               : "text" | "textarea" | "quill" | "array-strings" | "array-html"
 *   valueEs/En/Pt      : Valores actuales (string para text-types, array para array-strings)
 *   manualOverride     : { en: bool, pt: bool } — flags por idioma
 *
 * Events
 *   @change            : (lang, value)  — el padre actualiza state[lang][campo]
 *   @add-item          : (lang, item)   — solo array-strings, agregar
 *   @remove-item       : (lang, index)  — solo array-strings, quitar
 *
 * El padre es responsable de aplicar el cambio en Vuex (no muto props).
 */
export default {
  name: "I18nField",
  components: { VueEditor },
  props: {
    label: { type: String, default: "" },
    type: {
      type: String,
      default: "text",
      validator: v => ["text", "textarea", "quill", "array-strings", "array-html"].includes(v),
    },
    valueEs: { type: [String, Array, Number], default: "" },
    valueEn: { type: [String, Array, Number], default: "" },
    valuePt: { type: [String, Array, Number], default: "" },
    manualOverride: { type: Object, default: () => ({ en: false, pt: false }) },
    placeholder: { type: String, default: "" },
    rows: { type: [Number, String], default: 4 },
    quillToolbar: {
      type: Array,
      default: () => [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ color: [] }],
        ["blockquote", "link"],
        ["clean"],
      ],
    },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      activeLang: "es",
      newItem: "",
      lastQuillHtml: "",
    };
  },
  computed: {
    currentRaw() {
      if (this.activeLang === "es") return this.valueEs;
      if (this.activeLang === "en") return this.valueEn;
      return this.valuePt;
    },
    currentText() {
      const v = this.currentRaw;
      return typeof v === "string" ? v : v == null ? "" : String(v);
    },
    currentArray() {
      const v = this.currentRaw;
      return Array.isArray(v) ? v : [];
    },
  },
  methods: {
    isOverride(lang) {
      return !!(this.manualOverride && this.manualOverride[lang]);
    },
    isEmpty(lang) {
      const v = lang === "en" ? this.valueEn : this.valuePt;
      if (Array.isArray(v)) return v.length === 0;
      return !v || v === "";
    },
    emitChange(newValue) {
      this.$emit("change", this.activeLang, newValue);
    },
    onQuillChange() {
      // VueEditor no expone un valor en `text-change`. Leemos el HTML
      // directo del DOM (igual que el patron usado en EditExperiencia
      // existente).
      this.$nextTick(() => {
        const editor = this.$el.querySelector(".ql-editor");
        if (!editor) return;
        const html = editor.innerHTML;
        if (html === this.lastQuillHtml) return;
        this.lastQuillHtml = html;
        this.emitChange(html);
      });
    },
    onArrayHtmlChange() {
      this.$nextTick(() => {
        const editor = this.$el.querySelector(".ql-editor");
        if (!editor) return;
        const html = editor.innerHTML;
        if (html === this.newItem) return;
        this.newItem = html;
      });
    },
        addItem() {
      console.log("[I18nField] addItem called. newItem =", this.newItem, "type =", this.type);
      const raw = (this.newItem || "").trim();
      const stripped = raw.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
      if (!stripped) return;
      this.$emit("add-item", this.activeLang, raw);
      this.newItem = "";
    },
    removeItem(index) {
      if (!confirm("¿Eliminar este item?")) return;
      this.$emit("remove-item", this.activeLang, index);
    },
  },
};
</script>

<style scoped>
.i18n-field {
  margin-bottom: 1rem;
}

.i18n-field__label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.i18n-field__tabs {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0 0 0.5rem 0;
  border-bottom: 1px solid #dee2e6;
}

.i18n-field__tab {
  padding: 0.35rem 0.85rem;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  border: 1px solid transparent;
  border-bottom: none;
  margin-right: 2px;
  border-radius: 4px 4px 0 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.i18n-field__tab:hover {
  color: #495057;
  background: #f8f9fa;
}

.i18n-field__tab--active {
  color: #3d99ff;
  background: #fff;
  border-color: #dee2e6;
  border-bottom: 1px solid #fff;
  margin-bottom: -1px;
}

.i18n-field__tab--override small {
  color: #ffaa01;
  font-size: 0.85rem;
}

.i18n-field__pending {
  color: #adb5bd;
  font-size: 0.7rem;
  font-style: italic;
}

.i18n-field__array {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.5rem;
}

.i18n-field__items {
  list-style: none;
  padding: 0;
  margin: 0 0 0.5rem 0;
}

.i18n-field__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0.5rem;
  border-bottom: 1px solid #f1f3f5;
}

.i18n-field__item:last-child {
  border-bottom: none;
}

.i18n-field__remove {
  background: transparent;
  border: none;
  color: #d43b32;
  font-size: 1.2rem;
  cursor: pointer;
  line-height: 1;
  padding: 0 0.5rem;
}

.i18n-field__empty {
  margin: 0 0 0.5rem 0;
  color: #6c757d;
  font-size: 0.85rem;
}

.i18n-field__add {
  display: flex;
  gap: 0.5rem;
}

.i18n-field__add input {
  flex: 1;
}

.i18n-field__add--vertical {
  flex-direction: column;
  align-items: stretch;
}

.i18n-field__add--vertical .ql-container {
  min-height: 120px;
}

.i18n-field__add--vertical .btn {
  align-self: flex-end;
}
</style>
